import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { FiMail, FiLock, FiUser, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { login, register, loginWithGoogle, verify2FA, resend2FA } from '../services/api';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { register: registerForm, handleSubmit, formState: { errors } } = useForm();

  // Check if there's a pending negotiation
  const pendingNegotiation = sessionStorage.getItem('pendingNegotiation');

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const response = await login(data.email, data.password);
        
        if (response.data.requiresTwoFactorSetup && response.data.token) {
          // Login permitido para configurar o 2FA!
          authLogin(response.data.token, response.data.user);
          alert(response.data.message);
          completeLogin(response.data.user);
        } else if (response.data.requiresTwoFactor) {
          setPendingEmail(data.email);
          setShow2FA(true);
        } else {
          authLogin(response.data.token, response.data.user);
          completeLogin(response.data.user);
        }
      } else {
        const response = await register(data.email, data.password, data.name);
        authLogin(response.data.token, response.data.user);
        completeLogin(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao processar requisição');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (user) => {
    if (pendingNegotiation) {
      sessionStorage.removeItem('pendingNegotiation');
      navigate('/resultado');
    } else {
      if (user.role === 'admin' || user.email === 'admin@consultaseunome.com.br') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await verify2FA(pendingEmail, twoFACode);
      authLogin(response.data.token, response.data.user);
      completeLogin(response.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao verificar código');
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setLoading(true);
    setError('');
    try {
      await resend2FA(pendingEmail);
      setError('Código reenviado! Verifique seu email.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao reenviar código');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Implementar login real com Google OAuth (usar Firebase ou outro provedor)
      // Por enquanto, redirecionar para provider de autenticação real
      window.location.href = '/api/auth/google';
      
      if (pendingNegotiation) {
        sessionStorage.removeItem('pendingNegotiation');
        navigate('/resultado');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Erro ao fazer login com Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{show2FA ? 'Verificação 2FA' : (isLogin ? 'Entrar' : 'Criar Conta')} - ConsulteSeuNome</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">L</span>
              </div>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">
              {show2FA ? 'Verificação de Segurança' : (isLogin ? 'Bem-vindo de volta!' : 'Crie sua conta')}
            </h1>
            <p className="text-gray-600 mt-2">
              {show2FA 
                ? 'Digite o código de 6 dígitos do Google Authenticator'
                : (isLogin ? 'Entre para acessar seu dashboard e histórico' : 'Cadastre-se para salvar suas consultas')
              }
            </p>
          </div>

          <div className="card">
            {show2FA ? (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShow2FA(false);
                      setPendingEmail('');
                      setTwoFACode('');
                      setError('');
                    }}
                    className="flex items-center text-gray-500 hover:text-gray-700"
                  >
                    <FiArrowLeft className="mr-2" />
                    Voltar
                  </button>
                  <p className="text-sm text-gray-500">{pendingEmail}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código de Verificação
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="input-field pl-10 text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`p-3 rounded-lg text-sm ${
                      error.includes('reenviado') ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={loading || twoFACode.length < 6}
                  className="btn-primary w-full"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mx-auto"></div>
                  ) : (
                    'Verificar'
                  )}
                </button>


              </form>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        {...registerForm('name', { required: 'Nome é obrigatório' })}
                        className="input-field pl-10"
                        placeholder="Seu nome"
                      />
                    </div>
                    {errors.name && (
                      <p className="text-danger-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      {...registerForm('email', { 
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                      className="input-field pl-10"
                      placeholder="seu@email.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-danger-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...registerForm('password', { 
                        required: 'Senha é obrigatória',
                        minLength: {
                          value: 6,
                          message: 'Senha deve ter pelo menos 6 caracteres'
                        }
                      })}
                      className="input-field pl-10 pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-danger-500 text-sm mt-1">{errors.password.message}</p>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-3 bg-danger-50 text-danger-600 rounded-lg text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mx-auto"></div>
                  ) : (
                    isLogin ? 'Entrar' : 'Criar conta'
                  )}
                </button>
              </form>
            )}

            {!show2FA && (
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isLogin ? 'Não tem uma conta?' : 'Já tem uma conta?'}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setError('');
                    }}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {isLogin ? 'Criar conta' : 'Entrar'}
                  </button>
                </p>
              </div>
            )}
          </div>

          {!show2FA && (
            <p className="text-center text-gray-500 text-sm mt-6">
              Ao continuar, você concorda com nossos{' '}
              <Link to="/termos" target="_blank" className="text-primary-600 hover:underline">Termos de Uso</Link>
              {' '}e{' '}
              <Link to="/privacidade" target="_blank" className="text-primary-600 hover:underline">Política de Privacidade</Link>
            </p>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default Login;
